// Topic mapping for converting IDs to readable labels
export type TopicId =
  | "first_date_practice"
  | "hobbies_discussion"
  | "dreams_aspirations"
  | "healthy_lifestyle_wellness"
  | "overcoming_social_anxiety"
  | "share_cultural_experiences"
  | "travel_stories_adventure"
  | "anything"
  // Legacy/Deprecated topics (for compatibility with old rooms in DB)
  | "book_club_chat"
  | "career_goals_networking"
  | "animals_pets"
  | "music"
  | "tv_movies"
  | "self_improvement"
  | "short_term_goals"
  | "wins_successes"
  | "travel_adventures"
  | "knexo_first_timer"
  | "future_plans"
  | "healthy_habits";

export const TOPIC_LABELS: Record<TopicId, string> = {
  first_date_practice: "First date practice",
  hobbies_discussion: "Hobbies discussion",
  dreams_aspirations: "Dreams & aspirations",
  healthy_lifestyle_wellness: "Healthy lifestyle & wellness",
  overcoming_social_anxiety: "Overcoming social anxiety",
  share_cultural_experiences: "Share cultural experiences",
  travel_stories_adventure: "Travel stories & adventure",
  anything: "Anything",

  // Legacy/Deprecated topics
  book_club_chat: "Book club chat",
  career_goals_networking: "Career goals & networking",
  animals_pets: "Animals/pets",
  music: "Music",
  tv_movies: "TV/Movies",
  self_improvement: "Self improvement",
  short_term_goals: "Short term goals",
  wins_successes: "Wins/successes",
  travel_adventures: "Travel and adventures",
  knexo_first_timer: "Knexo first timer",
  future_plans: "Future plans",
  healthy_habits: "Healthy habits",
};

export const TOPIC_DESCRIPTIONS: Record<TopicId, string> = {
  first_date_practice: "Practice conversation skills and break the ice for dating.",
  hobbies_discussion: "Share your interests, hobbies, and how you spend free time.",
  dreams_aspirations: "Talk about your hopes, goals, and vision for the future.",
  healthy_lifestyle_wellness: "Discuss fitness, mental health, wellness, and self-care.",
  overcoming_social_anxiety:
    "Talk about best practices and challenges in social situations.",
  share_cultural_experiences:
    "Learn about someone with a different identity or background.",
  travel_stories_adventure: "Share your travel experiences and future destinations.",
  anything: "I'm open to any topic and seeing where the conversation goes.",

  // Legacy/Deprecated topics
  book_club_chat: "Talk about books, reading, stories, and literature.",
  career_goals_networking: "Discuss professional growth, jobs, and networking.",
  animals_pets: "Talk about your furry friends, pet stories, or animal love.",
  music: "Share your favorite songs, artists, or music experiences.",
  tv_movies: "Discuss shows, movies, or what you're watching lately.",
  self_improvement: "Talk about growth, habits, and becoming your best self.",
  short_term_goals:
    "Share what you're working on right now and your near-term plans.",
  wins_successes: "Celebrate your recent achievements, big or small.",
  travel_adventures:
    "Share travel stories, dream destinations, or adventure plans.",
  knexo_first_timer: "New here? Let's chat and get to know each other!",
  future_plans: "Discuss your dreams, aspirations, and where you're headed.",
  healthy_habits:
    "Talk about wellness, fitness, nutrition, and healthy routines.",
};

/**
 * Converts an array of topic IDs to readable labels
 * @param topicIds Array of topic IDs
 * @returns Array of readable topic labels
 */
export const getTopicLabels = (topicIds: TopicId[]): string[] => {
  return topicIds.map((id) => TOPIC_LABELS[id] || id);
};

/**
 * Converts an array of topic IDs to a comma-separated string of readable labels
 * @param topicIds Array of topic IDs
 * @returns Comma-separated string of topic labels
 */
export const getTopicLabelsString = (topicIds: TopicId[]): string => {
  return getTopicLabels(topicIds).join(", ");
};

// Topic objects for UI components
export type Topic = { id: TopicId; label: string; description: string };

export const TOPICS: Topic[] = [
  {
    id: "first_date_practice",
    label: TOPIC_LABELS.first_date_practice,
    description: TOPIC_DESCRIPTIONS.first_date_practice,
  },
  {
    id: "hobbies_discussion",
    label: TOPIC_LABELS.hobbies_discussion,
    description: TOPIC_DESCRIPTIONS.hobbies_discussion,
  },
  {
    id: "dreams_aspirations",
    label: TOPIC_LABELS.dreams_aspirations,
    description: TOPIC_DESCRIPTIONS.dreams_aspirations,
  },
  {
    id: "healthy_lifestyle_wellness",
    label: TOPIC_LABELS.healthy_lifestyle_wellness,
    description: TOPIC_DESCRIPTIONS.healthy_lifestyle_wellness,
  },
  {
    id: "overcoming_social_anxiety",
    label: TOPIC_LABELS.overcoming_social_anxiety,
    description: TOPIC_DESCRIPTIONS.overcoming_social_anxiety,
  },
  {
    id: "share_cultural_experiences",
    label: TOPIC_LABELS.share_cultural_experiences,
    description: TOPIC_DESCRIPTIONS.share_cultural_experiences,
  },
  {
    id: "travel_stories_adventure",
    label: TOPIC_LABELS.travel_stories_adventure,
    description: TOPIC_DESCRIPTIONS.travel_stories_adventure,
  },
  {
    id: "anything",
    label: TOPIC_LABELS.anything,
    description: TOPIC_DESCRIPTIONS.anything,
  },
];
